import React, { useState, useEffect } from 'react'
import {
  BellIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CalendarIcon,
  ArrowPathIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import api from '../../services/api'

interface UserItem {
  id: number
  email: string
  full_name: string
  username: string
  is_admin: boolean
}

interface NotificationLog {
  id: number
  user_id?: number
  title: string
  message: string
  notification_type: string
  is_read: boolean
  created_at: string
  send_email: boolean
  send_sms: boolean
  send_whatsapp: boolean
  send_push: boolean
}

const NotificationManagement: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationLog[]>([])
  const [filteredNotifications, setFilteredNotifications] = useState<NotificationLog[]>([])
  const [users, setUsers] = useState<UserItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'system' | 'recommendation' | 'alert' | 'marketing'>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState<NotificationLog | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)

  // Form state
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [notifType, setNotifType] = useState('system') // system, recommendation, alert, marketing
  const [targetType, setTargetType] = useState('all') // all, specific
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  
  // Channels
  const [sendInApp, setSendInApp] = useState(true)
  const [sendEmail, setSendEmail] = useState(false)
  const [sendSMS, setSendSMS] = useState(false)
  const [sendWhatsApp, setSendWhatsApp] = useState(false)
  
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    let filtered = notifications

    if (searchTerm) {
      filtered = filtered.filter(notif =>
        notif.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notif.message.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(notif => notif.notification_type === typeFilter)
    }

    setFilteredNotifications(filtered)
  }, [notifications, searchTerm, typeFilter])

  const loadData = async () => {
    try {
      setLoading(true)
      const [historyRes, usersRes] = await Promise.all([
        api.get('/notifications/admin/history'),
        api.get('/users/')
      ])
      setNotifications(historyRes.data)
      setUsers(usersRes.data.filter((u: UserItem) => !u.is_admin))
    } catch (error) {
      console.error('Error loading admin notifications data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSendNotification = async () => {
    if (!title || !message) return
    
    try {
      setSubmitting(true)
      await api.post('/notifications/send', {
        title,
        message,
        notification_type: notifType,
        target_type: targetType,
        user_ids: targetType === 'specific' ? selectedUserIds : null,
        send_in_app: sendInApp,
        send_email: sendEmail,
        send_sms: sendSMS,
        send_whatsapp: sendWhatsApp
      })
      
      // Reset form
      setTitle('')
      setMessage('')
      setNotifType('system')
      setTargetType('all')
      setSelectedUserIds([])
      setSendInApp(true)
      setSendEmail(false)
      setSendSMS(false)
      setSendWhatsApp(false)
      
      setShowCreateModal(false)
      // Reload logs
      await loadData()
    } catch (error) {
      console.error('Error sending notification:', error)
      alert('Failed to send notification. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'recommendation': return <InformationCircleIcon className="w-5 h-5 text-blue-400" />
      case 'alert': return <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
      case 'marketing': return <CheckCircleIcon className="w-5 h-5 text-green-400" />
      default: return <BellIcon className="w-5 h-5 text-gray-400" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Notification Management</h1>
          <p className="text-gray-400 mt-1">Send and manage user notifications</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={loadData}>
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Send Notification
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{notifications.length}</p>
            <p className="text-sm text-gray-400">Total Logs</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">{notifications.filter(n => n.is_read).length}</p>
            <p className="text-sm text-gray-400">Read By User</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-400">{notifications.filter(n => !n.is_read).length}</p>
            <p className="text-sm text-gray-400">Unread</p>
          </div>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            className="px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Types</option>
            <option value="system">System</option>
            <option value="recommendation">Recommendation</option>
            <option value="alert">Alert</option>
            <option value="marketing">Marketing</option>
          </select>
        </div>
      </Card>

      {/* Notifications Table */}
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-900 border-b border-dark-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Notification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {filteredNotifications.map((notification) => {
                const recipientUser = users.find(u => u.id === notification.user_id)
                return (
                  <tr key={notification.id} className="hover:bg-dark-750">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-white">{notification.title}</div>
                        <div className="text-xs text-gray-400 truncate max-w-xs">{notification.message}</div>
                        <div className="text-[10px] text-gray-500 mt-1">
                          Recipient: {recipientUser ? `${recipientUser.full_name} (${recipientUser.email})` : `User ID: ${notification.user_id}`}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-gray-300">
                        {getTypeIcon(notification.notification_type)}
                        <span className="ml-2 text-sm text-white capitalize">{notification.notification_type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        notification.is_read 
                          ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                          : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                      }`}>
                        {notification.is_read ? 'Read' : 'Unread'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1 text-gray-500" />
                        <div>{new Date(notification.created_at).toLocaleString()}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedNotification(notification)
                          setShowDetailModal(true)
                        }}
                      >
                        <EyeIcon className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {filteredNotifications.length === 0 && (
            <div className="text-center py-12">
              <BellIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No sent notifications found</p>
            </div>
          )}
        </div>
      </Card>

      {/* Send Notification Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative border border-dark-700 w-full max-w-lg shadow-2xl rounded-2xl bg-dark-800 p-6">
            <div className="flex justify-between items-center mb-4 border-b border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-white">Send New Notification</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white text-xl font-semibold"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter notification title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  placeholder="Enter notification message..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
                  <select
                    value={notifType}
                    onChange={(e) => setNotifType(e.target.value)}
                    className="w-full px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  >
                    <option value="system">System</option>
                    <option value="recommendation">Recommendation</option>
                    <option value="alert">Alert</option>
                    <option value="marketing">Marketing</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Recipient Target</label>
                  <select
                    value={targetType}
                    onChange={(e) => setTargetType(e.target.value)}
                    className="w-full px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  >
                    <option value="all">All Users</option>
                    <option value="specific">Specific Users</option>
                  </select>
                </div>
              </div>

              {/* Specific users select list */}
              {targetType === 'specific' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Select Recipients</label>
                  <div className="border border-dark-600 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2 bg-dark-900">
                    {users.map(user => (
                      <label key={user.id} className="flex items-center space-x-2 text-sm text-gray-300 hover:text-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUserIds(prev => [...prev, user.id])
                            } else {
                              setSelectedUserIds(prev => prev.filter(id => id !== user.id))
                            }
                          }}
                          className="rounded border-dark-600 text-primary-500 focus:ring-primary-500"
                        />
                        <span>{user.full_name} ({user.email})</span>
                      </label>
                    ))}
                    {users.length === 0 && (
                      <p className="text-xs text-gray-500 italic text-center py-2">No users available</p>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400">{selectedUserIds.length} user(s) selected</p>
                </div>
              )}

              {/* Delivery Channels */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">Delivery Channels</label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendInApp}
                      onChange={(e) => setSendInApp(e.target.checked)}
                      className="rounded border-dark-600 text-primary-500 focus:ring-primary-500"
                    />
                    <span>In-App Notification</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="rounded border-dark-600 text-primary-500 focus:ring-primary-500"
                    />
                    <span>Email Notification</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendSMS}
                      onChange={(e) => setSendSMS(e.target.checked)}
                      className="rounded border-dark-600 text-primary-500 focus:ring-primary-500"
                    />
                    <span>SMS Alert</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendWhatsApp}
                      onChange={(e) => setSendWhatsApp(e.target.checked)}
                      className="rounded border-dark-600 text-primary-500 focus:ring-primary-500"
                    />
                    <span>WhatsApp Message</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6 border-t border-dark-700 pt-4 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendNotification}
                disabled={submitting || !title || !message || (targetType === 'specific' && selectedUserIds.length === 0)}
              >
                {submitting ? 'Sending...' : 'Send Notification'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Detail Modal */}
      {showDetailModal && selectedNotification && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative border border-dark-700 w-full max-w-lg shadow-2xl rounded-2xl bg-dark-800 p-6">
            <div className="flex justify-between items-center mb-4 border-b border-dark-700 pb-3">
              <h3 className="text-lg font-bold text-white">Notification Details</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-white text-xl font-semibold"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase">Title</label>
                <p className="text-sm font-semibold text-white mt-0.5">{selectedNotification.title}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase">Message</label>
                <p className="text-sm text-gray-200 mt-0.5 whitespace-pre-wrap leading-relaxed">{selectedNotification.message}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 border-t border-b border-dark-700 py-3 my-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-400 uppercase">Type</label>
                  <span className="text-xs text-white font-medium capitalize block mt-0.5">{selectedNotification.notification_type}</span>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-400 uppercase">Read Status</label>
                  <span className={`inline-block text-xs font-medium rounded-full mt-0.5 ${
                    selectedNotification.is_read ? 'text-green-400' : 'text-yellow-400'
                  }`}>
                    {selectedNotification.is_read ? 'Read by user' : 'Unread'}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-semibold text-gray-400 uppercase">Delivery Channels Logged</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                    selectedNotification.send_email ? 'border-green-500/30 text-green-400 bg-green-500/5' : 'border-dark-600 text-gray-500'
                  }`}>
                    Email
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                    selectedNotification.send_sms ? 'border-green-500/30 text-green-400 bg-green-500/5' : 'border-dark-600 text-gray-500'
                  }`}>
                    SMS
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                    selectedNotification.send_whatsapp ? 'border-green-500/30 text-green-400 bg-green-500/5' : 'border-dark-600 text-gray-500'
                  }`}>
                    WhatsApp
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 border-t border-dark-700 pt-4">
              <Button onClick={() => setShowDetailModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationManagement
